import { Check, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SERVICE_TYPES, formatDate, formatMoney, greetingHref, intervalLabel, statusOf, whatsappHref } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";
import { WhatsAppButton } from "@/components/app/WhatsAppButton";
import { NoticeButton } from "@/components/app/NoticeButton";
import { ArchiveButton } from "@/components/app/ArchiveButton";

const TONES = {
  realizado: "border-emerald-500/60 bg-emerald-500/[0.12] hover:bg-emerald-500/[0.18]",
  vencido: "border-rose-500/60 bg-rose-500/[0.12] hover:bg-rose-500/[0.18]",
  breve: "border-amber-500/60 bg-amber-500/[0.12] hover:bg-amber-500/[0.18]",
  agendado: "border-sky-500/50 bg-sky-500/[0.08] hover:bg-sky-500/[0.14]",
};

const BADGES = {
  realizado: "bg-emerald-500 text-emerald-950 border-emerald-400",
  agendado: "bg-sky-500/25 text-sky-100 border-sky-400/60",
  breve: "bg-amber-400 text-amber-950 border-amber-300",
  vencido: "bg-rose-500 text-white border-rose-400",
};

const badgeText = (st) => {
  if (st.key === "realizado") return "Realizado";
  if (st.key === "vencido") return `Vencido há ${st.days}d`;
  if (st.key === "breve") return st.days === 0 ? "Vence hoje" : `Vence em ${st.days}d`;
  return "Agendado";
};

const intervalText = (s) => intervalLabel(s.interval_value, s.interval_unit);

export const StatusBadge = ({ s, today, testid }) => {
  const st = statusOf(s, today);
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${BADGES[st.key]}`} data-testid={testid}>{badgeText(st)}</span>;
};

export const ServiceRow = ({ s }) => {
  const { today, openEdit, openComplete, requestDelete, openClient, contacts, company, messaging } = useServices();
  const t = SERVICE_TYPES[s.service_type];
  const Icon = t.icon;
  const c = contacts?.[s.client_key];
  const phone = c?.phone || "";
  const waHref = s.status === "agendado" ? whatsappHref({ ...s, phone }, company, messaging?.visit_template) : greetingHref(phone, c?.contact_person || s.client_name, company, messaging?.greeting_template);
  return (
    <div
      className={`flex flex-col gap-3 rounded-xl border p-4 transition-colors duration-200 sm:flex-row sm:items-start ${TONES[statusOf(s, today).key]}`}
      style={{ borderLeft: `4px solid ${t.hex}` }}
      data-testid={`service-row-${s.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => openClient(s.client_name)} className="truncate text-left font-semibold hover:underline" data-testid={`service-client-link-${s.id}`}>
            {s.client_name}
          </button>
          <StatusBadge s={s} today={today} testid={`service-status-${s.id}`} />
          {s.archived && <span className="rounded-full border border-slate-500 px-2 py-0.5 text-[11px] font-semibold text-slate-300" data-testid={`service-archived-${s.id}`}>Arquivado</span>}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-sm text-slate-300">
          <Icon className="h-3.5 w-3.5" style={{ color: t.hex }} /> {t.label}
          <span className="text-slate-500">· {s.interval_value && s.interval_unit ? intervalText(s) : t.validity}</span>
          {s.price != null && <span className="font-mono text-slate-300" data-testid={`service-price-${s.id}`}>· {formatMoney(s.price)}</span>}
        </div>
        <div className="mt-1 font-mono text-xs text-slate-400" data-testid={`service-dates-${s.id}`}>
          {s.status === "realizado"
            ? `Executado em ${formatDate(s.execution_date)} · válido até ${formatDate(s.expiry_date)}`
            : `Agendado para ${formatDate(s.execution_date)}`}
        </div>
        {s.notes && <p className="mt-1.5 text-xs italic text-slate-400">{s.notes}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1">
        <WhatsAppButton compact href={waHref} testid={`service-whatsapp-${s.id}`} />
        {s.status === "agendado" && !s.archived && <NoticeButton s={s} compact />}
        {s.status === "agendado" && !s.archived && s.execution_date < today && <ArchiveButton s={s} />}
        {s.status === "agendado" && (
          <Button size="sm" onClick={() => openComplete(s)} className="rounded-full" data-testid={`mark-done-button-${s.id}`}>
            <Check className="mr-1 h-3.5 w-3.5" /> Realizar
          </Button>
        )}
        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)} data-testid={`edit-service-button-${s.id}`} aria-label="Editar">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400 hover:text-rose-300" onClick={() => requestDelete(s)} data-testid={`delete-service-button-${s.id}`} aria-label="Excluir">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
